import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  AccountSpendLimits,
  PoolWindow,
  PoolWindowKind,
  PoolWindowStatus,
  PoolWindowUnit,
  getAccountSpendLimits,
  setAccountSpendLimits,
} from '../../services/rbacService';
import { useUsageVisibility } from '../../hooks/useUsageVisibility';
import { formatResetTime } from '../Activity/activityTheme';
import { usdToWu, formatWu, WU_LABEL } from '../../config/workUnits';

/**
 * Spending & usage limits — the ORGANIZATION's guardrails (2026-08-14 owner
 * directive, replacing the per-run and planning-stage caps that shipped here
 * earlier the same day).
 *
 * The owner asked for one overarching account-wide control, not per-run
 * guardrails: "there should be not a per run spend limit or a planning stage
 * limit. there should be an overall limit … much like the controls over the
 * roles." This card therefore carries exactly one thing — the ORGANIZATION
 * SPENDING LIMITS: up to ten pooled caps, each over a period the owner chooses
 * (minutes, hours, days, weeks, or a calendar day/week/month). Everyone's work
 * counts against every limit, on existing accounts too, enforced server-side in
 * functions/prest0nSession/accountSpendGate.ts.
 *
 * The balance stop (work pauses on an empty wallet) is still a hard platform
 * rule enforced there for every organization — it simply has no UI, because
 * nobody can change it and a tile restating it earned none of this space.
 *
 * Permission: owners always; otherwise usage.pool-limits.manage to change and
 * usage.pool-limits.view to read (administrators hold both by default). Anyone
 * without either permission never sees the card.
 */

/** Period choices offered in the UI, in the order they read as a sentence. */
const PERIOD_OPTIONS: Array<{
  value: string;
  label: string;
  kind: PoolWindowKind;
  unit?: PoolWindowUnit;
}> = [
  { value: 'minutes', label: 'minutes', kind: 'rolling', unit: 'minutes' },
  { value: 'hours', label: 'hours', kind: 'rolling', unit: 'hours' },
  { value: 'days', label: 'days', kind: 'rolling', unit: 'days' },
  { value: 'weeks', label: 'weeks', kind: 'rolling', unit: 'weeks' },
];

const CALENDAR_OPTIONS: Array<{ value: PoolWindowKind; label: string }> = [
  { value: 'calendar-day', label: 'each calendar day' },
  { value: 'calendar-week', label: 'each calendar week' },
  { value: 'calendar-month', label: 'each calendar month' },
];

const MAX_POOL_WINDOWS = 10;
const DEFAULT_POOL_USD = 500;
const DEFAULT_POOL_AMOUNT = 7;
const DEFAULT_POOL_UNIT: PoolWindowUnit = 'days';

const usd = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Card = styled.section`
  background: white;
  border: 1px solid var(--brand-line);
  border-radius: 1rem;
  box-shadow: 0 0.375rem 1.25rem rgba(0, 0, 0, 0.04);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;

  @media (min-width: 768px) {
    padding: 2rem;
    border-radius: 1.25rem;
    margin-bottom: 2rem;
  }

  @media (min-width: 1024px) {
    &:hover {
      box-shadow: 0 0.75rem 2rem rgba(0, 0, 0, 0.08);
      transform: translateY(-2px);
    }
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.375rem;
`;

const TitleWrap = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin: 0;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--brand-text);
  text-transform: lowercase;
  letter-spacing: -0.01em;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const TitleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  color: white;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const OwnerPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.75rem;
  background: #FBF8F0;
  border: 1px solid #E8DFC4;
  border-radius: 999px;
  color: #8B7333;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: lowercase;
  letter-spacing: 0.03em;

  svg {
    width: 11px;
    height: 11px;
  }
`;

const Caption = styled.p`
  margin: 0 0 1.25rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.85rem;
  color: var(--brand-text-secondary);
  line-height: 1.5;
  text-transform: lowercase;
`;

/**
 * The limits own the whole card now, so the body is one full-width panel
 * instead of a half-width tile in a two-up grid.
 */
const LimitsPanel = styled.div<{ $active: boolean }>`
  border: 2px solid ${props => props.$active ? '#E8DFC4' : 'var(--brand-line)'};
  background: ${props => props.$active ? '#FDFCF7' : 'var(--brand-canvas)'};
  border-radius: 12px;
  padding: 1.125rem 1.25rem 1.25rem;
  transition: border-color 0.2s ease, background 0.2s ease;

  @media (min-width: 768px) {
    padding: 1.375rem 1.5rem 1.5rem;
  }
`;

const TileTitle = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--brand-text);
  text-transform: lowercase;
`;

const TileHint = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
  text-transform: lowercase;
`;

const TileHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const InputWrap = styled.div`
  position: relative;
  max-width: 240px;
`;

const InputAffix = styled.span<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.$side === 'left' ? 'left: 12px;' : 'right: 12px;'}
  color: #6b7280;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.85rem;
  text-transform: lowercase;
  pointer-events: none;
`;

const LimitInput = styled.input<{ $padLeft?: boolean; $padRight?: boolean }>`
  width: 100%;
  padding: 0.7rem 0.875rem;
  ${props => props.$padLeft ? 'padding-left: 1.9rem;' : ''}
  ${props => props.$padRight ? 'padding-right: 3.6rem;' : ''}
  border: 2px solid var(--brand-line);
  border-radius: 8px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.95rem;
  background: white;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  min-height: 44px;

  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.12);
  }

  &:disabled { opacity: 0.6; }
`;

const EquivText = styled.span`
  display: block;
  margin-top: 0.5rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.75rem;
  color: #8B7333;
  text-transform: lowercase;
`;

const UsageText = styled.span`
  display: block;
  margin-top: 0.25rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: lowercase;
  font-variant-numeric: tabular-nums;
`;

const OffText = styled.span`
  display: block;
  margin-top: 0.875rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.78rem;
  color: #888;
  font-style: italic;
  text-transform: lowercase;
`;

const FootRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1.25rem;
`;

const InfoNote = styled.div`
  flex: 1 1 260px;
  background: var(--brand-canvas);
  border-left: 4px solid var(--brand-gold);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.78rem;
  color: var(--brand-text-secondary);
  line-height: 1.5;
  text-transform: lowercase;
`;

const StatusText = styled.span<{ $state: 'saving' | 'saved' | 'error' }>`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: lowercase;
  color: ${props =>
    props.$state === 'saved' ? '#2e7d32'
    : props.$state === 'error' ? '#b91c1c'
    : '#8B7333'};
`;

/**
 * With the full card width available the rows sit two-across on a desktop
 * wallet page and stack on anything narrower.
 */
const WindowList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.875rem;
  margin-top: 1rem;

  @media (min-width: 860px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }
`;

const WindowRow = styled.div`
  border: 1px solid #E8DFC4;
  background: white;
  border-radius: 10px;
  padding: 0.875rem 1rem 1rem;
`;

const RowTop = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RemoveButton = styled.button`
  flex: none;
  margin-left: auto;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid var(--brand-line);
  background: white;
  color: #888;
  font-size: 0.9rem;
  line-height: 1;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #C0392B;
    color: #C0392B;
    background: #FDECEC;
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const AddButton = styled.button`
  margin-top: 0.875rem;
  padding: 0.45rem 0.9rem;
  border-radius: 0.5rem;
  border: 1px dashed var(--brand-gold);
  background: transparent;
  color: #8B7333;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  text-transform: lowercase;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #FBF8F0;
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PeriodRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const PeriodSelect = styled.select`
  flex: 1;
  min-width: 7rem;
  padding: 0.5rem 0.6rem;
  border: 1px solid var(--brand-line);
  border-radius: 0.5rem;
  background: white;
  font-family: inherit;
  font-size: 0.85rem;
  color: var(--brand-text);

  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.12);
  }

  &:disabled { opacity: 0.6; }
`;

const PeriodNumber = styled.input`
  width: 4.5rem;
  padding: 0.5rem 0.6rem;
  border: 1px solid var(--brand-line);
  border-radius: 0.5rem;
  font-family: inherit;
  font-size: 0.85rem;
  color: var(--brand-text);

  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.12);
  }

  &:disabled { opacity: 0.6; }
`;

const SaveButton = styled.button`
  padding: 0.55rem 1.1rem;
  border-radius: 0.5rem;
  border: none;
  background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  color: var(--brand-text);
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  text-transform: lowercase;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, var(--brand-gold-hover) 0%, #A07D3A 100%);
  }

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// ── component ────────────────────────────────────────────────────────────────

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/** One editable limit row (drafts kept as strings so typing stays free). */
interface WindowDraft {
  id: string;
  usdDraft: string;
  periodMode: 'rolling' | PoolWindowKind;
  amountDraft: string;
  unit: PoolWindowUnit;
}

let poolIdCounter = 0;
/** `pool-{timestamp}` per the id convention; the counter keeps rapid adds unique. */
function freshPoolId(existing: WindowDraft[]): string {
  let id = `pool-${Date.now()}`;
  while (existing.some((w) => w.id === id)) {
    poolIdCounter += 1;
    id = `pool-${Date.now()}-${poolIdCounter}`;
  }
  return id;
}

function draftFromWindow(w: PoolWindow): WindowDraft {
  return {
    id: w.id,
    usdDraft: String(w.costUsdLimit),
    periodMode: w.kind === 'rolling' ? 'rolling' : w.kind,
    amountDraft: String(w.amount ?? DEFAULT_POOL_AMOUNT),
    unit: (w.unit as PoolWindowUnit) ?? DEFAULT_POOL_UNIT,
  };
}

/** The PoolWindow a draft row describes (null while the row is incomplete). */
function windowFromDraft(d: WindowDraft): PoolWindow | null {
  const cost = Number(d.usdDraft);
  if (!Number.isFinite(cost) || cost <= 0) return null;
  if (d.periodMode === 'rolling') {
    const amount = Math.floor(Number(d.amountDraft));
    if (!Number.isFinite(amount) || amount <= 0) return null;
    return { id: d.id, kind: 'rolling', amount, unit: d.unit, costUsdLimit: cost };
  }
  return { id: d.id, kind: d.periodMode, costUsdLimit: cost };
}

function periodPhrase(d: WindowDraft): string {
  if (d.periodMode !== 'rolling') {
    return CALENDAR_OPTIONS.find((o) => o.value === d.periodMode)?.label ?? 'each period';
  }
  const amount = Math.floor(Number(d.amountDraft));
  if (!Number.isFinite(amount) || amount <= 0) return 'this period';
  const noun = amount === 1 ? d.unit.replace(/s$/, '') : d.unit;
  return `every ${amount} ${noun}`;
}

interface UsageLimitsCardProps {
  /** Kept for call-site compatibility; the account is resolved server-side. */
  uid?: string;
}

const UsageLimitsCard: React.FC<UsageLimitsCardProps> = () => {
  const visibility = useUsageVisibility();
  const canView = visibility.canViewPoolLimits;
  const canManage = visibility.canManagePoolLimits;

  const [loaded, setLoaded] = useState(false);
  // A failed read must NOT render the editor: it would claim "no limit"
  // while limits may still be enforced server-side.
  const [loadFailed, setLoadFailed] = useState(false);
  const [windowsStatus, setWindowsStatus] = useState<PoolWindowStatus[]>([]);

  // Pool limits — the editable list (up to MAX_POOL_WINDOWS rows).
  const [windows, setWindows] = useState<WindowDraft[]>([]);

  const [saveState, setSaveState] = useState<SaveState>('idle');
  const savedTimerRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
  }, []);

  const applyConfig = useCallback((config: AccountSpendLimits) => {
    setWindows((config.windows ?? []).slice(0, MAX_POOL_WINDOWS).map(draftFromWindow));
  }, []);

  useEffect(() => {
    if (!canView) return;
    let canceled = false;
    (async () => {
      try {
        const status = await getAccountSpendLimits();
        if (canceled) return;
        applyConfig(status.config);
        setWindowsStatus(status.windowsStatus ?? []);
        setLoadFailed(false);
      } catch (e) {
        if (!canceled) setLoadFailed(true);
      } finally {
        if (!canceled) setLoaded(true);
      }
    })();
    return () => {
      canceled = true;
    };
  }, [canView, applyConfig]);

  const patchWindow = useCallback((id: string, patch: Partial<WindowDraft>) => {
    setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  }, []);

  const removeWindow = useCallback((id: string) => {
    setWindows((prev) => prev.filter((w) => w.id !== id));
  }, []);

  const addWindow = useCallback(() => {
    setWindows((prev) => {
      if (prev.length >= MAX_POOL_WINDOWS) return prev;
      return [
        ...prev,
        {
          id: freshPoolId(prev),
          usdDraft: String(DEFAULT_POOL_USD),
          periodMode: 'rolling',
          amountDraft: String(DEFAULT_POOL_AMOUNT),
          unit: DEFAULT_POOL_UNIT,
        },
      ];
    });
  }, []);

  const statusById = useMemo(() => {
    const map = new Map<string, PoolWindowStatus>();
    for (const s of windowsStatus) map.set(s.id, s);
    return map;
  }, [windowsStatus]);

  const save = useCallback(async () => {
    if (!canManage) return;
    setSaveState('saving');
    try {
      // Rows without a usable $ amount are treated as not-yet-configured and
      // left out of the save (same forgiveness the single-limit editor had).
      const payload = windows
        .map(windowFromDraft)
        .filter((w): w is PoolWindow => w !== null);
      // The balance stop is a hard platform rule — the server owns it; the
      // client sends ONLY the pool windows.
      await setAccountSpendLimits({ windows: payload });
      setSaveState('saved');
      if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
      savedTimerRef.current = window.setTimeout(() => setSaveState('idle'), 2500);
    } catch {
      setSaveState('error');
    }
  }, [canManage, windows]);

  // Members without either permission never see the card at all.
  if (!canView) return null;
  if (!loaded) return null;

  return (
    <Card aria-label="spending and usage limits">
      <HeaderRow>
        <TitleWrap>
          <TitleIcon aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="21" y1="4" x2="14" y2="4"></line>
              <line x1="10" y1="4" x2="3" y2="4"></line>
              <line x1="21" y1="12" x2="12" y2="12"></line>
              <line x1="8" y1="12" x2="3" y2="12"></line>
              <line x1="21" y1="20" x2="16" y2="20"></line>
              <line x1="12" y1="20" x2="3" y2="20"></line>
              <line x1="14" y1="2" x2="14" y2="6"></line>
              <line x1="8" y1="10" x2="8" y2="14"></line>
              <line x1="16" y1="18" x2="16" y2="22"></line>
            </svg>
          </TitleIcon>
          spending &amp; usage limits
        </TitleWrap>
        <OwnerPill>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
          </svg>
          {canManage ? 'billing setting' : 'view only'}
        </OwnerPill>
      </HeaderRow>
      <Caption>
        the whole organization shares these guardrails — every member&apos;s work counts against
        the same limits, and they apply to work already underway
      </Caption>

      {loadFailed ? (
        <InfoNote role="alert">
          couldn&apos;t load your limits — anything set before is still enforced. refresh to try again.
        </InfoNote>
      ) : (
        // Organization spending limits (multi-window) — the whole card body.
        <LimitsPanel $active={windows.length > 0}>
          <TileHead>
            <TileTitle>organization spending limits</TileTitle>
            <TileHint>
              pooled caps across everyone, each for a period you choose — up to {MAX_POOL_WINDOWS}
            </TileHint>
          </TileHead>

          {windows.length === 0 && (
            <OffText>no caps — the organization can spend whatever the wallet holds</OffText>
          )}

          {windows.length > 0 && (
            <WindowList>
              {windows.map((w) => {
                const cost = Number(w.usdDraft);
                const wu = Number.isFinite(cost) && cost > 0 ? usdToWu(cost) : 0;
                const live = statusById.get(w.id);
                return (
                  <WindowRow key={w.id}>
                    <RowTop>
                      <InputWrap style={{ flex: '1 1 8rem' }}>
                        <InputAffix $side="left">$</InputAffix>
                        <LimitInput
                          $padLeft
                          type="number"
                          min={1}
                          step="10"
                          value={w.usdDraft}
                          disabled={!canManage}
                          onChange={(e) => patchWindow(w.id, { usdDraft: e.target.value })}
                          aria-label="spending limit in usd"
                        />
                      </InputWrap>
                      <RemoveButton
                        type="button"
                        disabled={!canManage}
                        onClick={() => removeWindow(w.id)}
                        aria-label="remove this limit"
                        title="remove this limit"
                      >
                        ✕
                      </RemoveButton>
                    </RowTop>
                    <PeriodRow>
                      <PeriodSelect
                        value={w.periodMode}
                        disabled={!canManage}
                        onChange={(e) => patchWindow(w.id, { periodMode: e.target.value as any })}
                        aria-label="limit period type"
                      >
                        <option value="rolling">every…</option>
                        {CALENDAR_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </PeriodSelect>
                      {w.periodMode === 'rolling' && (
                        <>
                          <PeriodNumber
                            type="number"
                            min={1}
                            step="1"
                            value={w.amountDraft}
                            disabled={!canManage}
                            onChange={(e) => patchWindow(w.id, { amountDraft: e.target.value })}
                            aria-label="how many periods"
                          />
                          <PeriodSelect
                            value={w.unit}
                            disabled={!canManage}
                            onChange={(e) => patchWindow(w.id, { unit: e.target.value as PoolWindowUnit })}
                            aria-label="period unit"
                          >
                            {PERIOD_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </PeriodSelect>
                        </>
                      )}
                    </PeriodRow>
                    <EquivText>
                      {wu > 0
                        ? `${usd(cost)} ≈ ${formatWu(wu)} ${WU_LABEL} — ${periodPhrase(w)}`
                        : `enter an amount — ${periodPhrase(w)}`}
                    </EquivText>
                    {live && (
                      <UsageText>
                        {usd(live.usedUsd)} of {usd(live.limitUsd)} used — resets {formatResetTime(live.resetMs)}
                      </UsageText>
                    )}
                  </WindowRow>
                );
              })}
            </WindowList>
          )}

          {canManage && (
            <AddButton
              type="button"
              onClick={addWindow}
              disabled={windows.length >= MAX_POOL_WINDOWS}
            >
              + add another limit
            </AddButton>
          )}
        </LimitsPanel>
      )}

      {!loadFailed && canManage && (
        <FootRow>
          <SaveButton type="button" onClick={save} disabled={saveState === 'saving'}>
            {saveState === 'saving' ? 'saving…' : 'save limits'}
          </SaveButton>
          {saveState !== 'idle' && saveState !== 'saving' && (
            <StatusText $state={saveState === 'saved' ? 'saved' : 'error'} role="status">
              {saveState === 'saved' ? 'saved ✓' : 'save failed — try again'}
            </StatusText>
          )}
        </FootRow>
      )}
      {!loadFailed && !canManage && (
        <FootRow>
          <InfoNote>these limits are set by an owner or administrator.</InfoNote>
        </FootRow>
      )}
    </Card>
  );
};

export default UsageLimitsCard;
